import { FirebaseError } from 'firebase/app'
import { useEffect, useMemo } from 'react'
import { useController, useFieldArray, useForm } from 'react-hook-form'
import { LimitationType } from '../../assets/draft/LimitedAuthorizationSetting'
import { ArticleDraft, Timestamp, updateDoc } from '../../firebase'
import { useAuthState } from '../../firebase/hooks'
import { useMacros, useProfile, useReferences } from '../../hooks'
import { addCollaborator } from '../../models/draft'
import { IS_PROD } from '../../utils'

export type ArticleDraftFields = {
  title: string
  body: string
  editable_uids: { value: string }[]
  limitation_type: LimitationType
}

export const useArticleDraftForm = (draft: ArticleDraft) => {
  const { user } = useAuthState()
  const { profile: authorProfile } = useProfile(draft.created_by, null)
  const { macros } = useMacros(draft.ref)
  const { references } = useReferences(draft.ref)

  useEffect(() => {
    if (!user) return
    if (user.uid === draft.created_by) return
    if (draft.collaborator_uids.includes(user.uid)) return
    void addCollaborator(draft, user.uid)
  }, [user])

  const {
    formState: { errors, isDirty, isValid },
    control,
    setValue,
    getValues,
  } = useForm<ArticleDraftFields>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      title: draft.title,
      body: draft.body,
      editable_uids: draft.editability_details.authorized_uids.map((uid) => ({
        value: uid,
      })),
      limitation_type: draft.editability_details.authorized_by_url
        ? 'limited_by_url'
        : 'limited_by_uids',
    },
  })

  const {
    field: { onChange: handleTitleChange, value: title },
  } = useController({
    name: 'title',
    control,
    rules: { required: true, pattern: /[\S]/ },
  })

  const {
    field: { onChange: handleBodyChange, value: body },
  } = useController({
    name: 'body',
    control,
    rules: { required: true },
  })

  const { field: limitationTypeField } = useController({
    control,
    name: 'limitation_type',
  })

  const editableUidsFieldArray = useFieldArray({
    control,
    name: 'editable_uids',
  })

  const saveTitleChange = async () => {
    const { title } = getValues()
    await updateDoc(draft.ref, {
      title,
      updated_by_user_at: Timestamp.now(),
    })
  }

  const saveBodyChange = async () => {
    const { body } = getValues()
    try {
      await updateDoc(draft.ref, {
        body,
        updated_by_user_at: Timestamp.now(),
      })
    } catch (error) {
      // 編集からページから削除ボタンを押して離れた場合に
      // 既に下書き削除されておりエラーになるので、その場合は無視する
      if (error instanceof FirebaseError && error.code === 'not-found')
        console.warn(error.code)
      else throw error
    }
  }

  const saveEditabilityChange = async () => {
    const editableUidsFieldArrayValues = editableUidsFieldArray.fields.map(
      ({ value }) => value
    )
    const limitationTypeFieldValue = limitationTypeField.value
    await updateDoc(draft.ref, {
      editability_details: {
        authorized_uids: editableUidsFieldArrayValues,
        authorized_by_url: limitationTypeFieldValue === 'limited_by_url',
      },
      updated_by_user_at: Timestamp.now(),
    })
  }

  const handleSave = async () => {
    const { title, body } = getValues()
    await updateDoc(draft.ref, {
      title,
      body,
      updated_by_user_at: Timestamp.now(),
    })
  }

  useEffect(() => {
    setValue('title', draft.title)
  }, [draft.title])

  useEffect(() => {
    setValue('body', draft.body)
  }, [draft.body])

  useEffect(() => {
    const {
      authorized_by_url: authorizedByUrl,
      authorized_uids: authorizedUids,
    } = draft.editability_details
    setValue(
      'editable_uids',
      authorizedUids.map((uid) => ({ value: uid }))
    )
    setValue(
      'limitation_type',
      authorizedByUrl ? 'limited_by_url' : 'limited_by_uids'
    )
  }, [draft.editability_details])

  const room = useMemo(() => {
    if (!user || !authorProfile) return undefined
    // 著書が無料プランのユーザーの場合は、共同編集のルームを設定しない
    // TODO: 本来は plan ではなく features をみるべきだが、features は現状アクセス困難なため妥協
    if (authorProfile.plan === 'Free') return undefined
    // 共同編集の設定がされていない場合は、共同編集のルームを設定しない
    if (
      !draft.editability_details.authorized_by_url &&
      draft.editability_details.authorized_uids.length === 0
    )
      return undefined
    // 現在のユーザにアクセス権がない場合は、共同編集のルームを設定しない
    if (
      !draft.editability_details.authorized_by_url &&
      !draft.editability_details.authorized_uids.includes(user.uid) &&
      user.uid !== draft.created_by
    )
      return undefined
    return `${IS_PROD ? 'prod' : 'dev'}/article_drafts/${draft.id}`
  }, [draft, user, authorProfile])

  const sharedPath = useMemo(
    () => `/article_drafts/${draft.id}/edit`,
    [draft.id]
  )

  return {
    macros,
    references,
    errors,
    isDirty,
    isValid,
    body,
    handleBodyChange,
    title,
    handleTitleChange,
    saveTitleChange,
    saveBodyChange,
    sharedPath,
    limitationTypeField,
    editableUidsFieldArray,
    saveEditabilityChange,
    handleSave,
    room,
  }
}
